import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import {
  Briefcase,
  Search,
  Users,
  MessageSquare,
  Star,
  Plus,
  Filter,
  TrendingUp,
  Award,
  Sparkles,
  FileText,
  MapPin,
  Clock,
  DollarSign,
  LogOut,
  Settings,
  Bell,
  Send,
  Bookmark,
  Heart,
  type LucideIcon,
  Database,
  BarChart3,
  Palette,
  Trash2,
  Eye,
  ThumbsUp,
  MousePointer,
  UserPlus,
  Image,
  Edit,
  Download,
  Upload,
  X,
  Shield,
  Calendar,
  AlertCircle,
  TrendingUp as TrendingUpIcon,
  Target,
  Flame,
  Paperclip,
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Progress } from '../ui/progress';
import { RoleBadge } from '../common/RoleBadge';
import { SkillTestManager } from './SkillTestManager';
import { ViewApplicants } from './ViewApplicants';
import { useSavedSearches, type SavedSearch, type NotificationCadence } from '../../hooks/useSavedSearches';
import { useEmployerDashboardSummary } from '../../hooks/useEmployerDashboardSummary';
import { useConversations, useConversationMessages, useSendConversationMessage, useConversationEvents } from '../../hooks/useConversations';
import { useEmployerApplicants } from '../../hooks/useEmployerApplicants';
import type { EmployerApplicant } from '../../api/employerApplicants';
import { deleteUpload, uploadCompanyAsset, type UploadedFileMeta } from '../../api/uploads';
import { uploadConversationAttachment, type ConversationSummary, type ConversationMessage } from '../../api/conversations';
import { useAuth } from '../../hooks/useAuth';
import { useEmployerJobs } from '../../hooks/useEmployerJobs';
import type { EmployerJob } from '../../api/employerJobs';
import { fetchEmployerBranding, saveEmployerBranding, type EmployerBrandingPayload } from '../../api/employerBranding';
import { useTalentSearch } from '../../hooks/useTalentSearch';
import type { TalentSearchResult } from '../../api/talentSearch';
import { useOccupationSearch } from '../../hooks/useOccupations';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchJobAnalytics, type JobAnalyticsRow } from '../../api/jobAnalytics';
import { Helmet } from 'react-helmet-async';
const SkeletonCard = ({ className }: { className?: string }) => (
  <div className={`animate-pulse rounded-2xl border border-white/10 bg-white/5 ${className ?? ''}`} />
);

const DEFAULT_JOB_ANALYTICS_ROWS: never[] = [];

type TalentPool = {
  id: string;
  name: string;
  criteria: string;
  candidateCount: number;
  matchRate: number;
  lastUpdated: string;
};

const TALENT_POOL_DEFINITIONS: Array<{
  id: string;
  name: string;
  criteria: string;
  filter: (candidate: TalentSearchResult) => boolean;
}> = [
  {
    id: 'returner',
    name: 'Returner Champions',
    criteria: 'Returner friendly + leadership ready',
    filter: (candidate) => candidate.returner,
  },
  {
    id: 'immediate',
    name: 'Immediate Availability',
    criteria: 'Availability marked as immediate',
    filter: (candidate) => candidate.availability?.toLowerCase().includes('immediate'),
  },
  {
    id: 'remote',
    name: 'Remote Ready',
    criteria: 'Remote/Hybrid locations',
    filter: (candidate) => candidate.location?.toLowerCase().includes('remote'),
  },
];

const buildTalentPools = (candidates: TalentSearchResult[]): TalentPool[] => {
  const updatedLabel = new Date().toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
  return TALENT_POOL_DEFINITIONS.map((definition) => {
    const matched = candidates.filter(definition.filter);
    const candidateCount = matched.length;
    const matchRate = candidateCount
      ? Math.round(
          matched.reduce((sum, candidate) => sum + (candidate.matchScore ?? 75), 0) / Math.max(candidateCount, 1),
        )
      : 75;
    return {
      id: definition.id,
      name: definition.name,
      criteria: definition.criteria,
      candidateCount,
      matchRate,
      lastUpdated: updatedLabel,
    };
  });
};

export function EmployerDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { initialTab } = (location.state as { initialTab?: string } | null) ?? {};
  const [activeTab, setActiveTab] = useState(initialTab ?? 'overview');
  const queryClient = useQueryClient();

  useEffect(() => {
    if (initialTab) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [initialTab, location.pathname, navigate]);

  useEffect(() => {
    const prefixes =
      activeTab === 'overview'
        ? ['employer-dashboard', 'employer-jobs', 'employer-applicants', 'talent-search']
        : activeTab === 'jobs'
        ? ['employer-jobs']
        : activeTab === 'applicants'
        ? ['employer-applicants']
        : activeTab === 'candidates'
        ? ['talent-search']
        : ['employer-dashboard'];
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey?.[0];
        return typeof key === 'string' && prefixes.some((p) => key === p);
      },
    });
  }, [activeTab, queryClient]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState<any>(null);
  const [shortlistedIds, setShortlistedIds] = useState<string[]>(['1', '3']);
  const [messageText, setMessageText] = useState('');
  const [companyPhotos, setCompanyPhotos] = useState<UploadedFileMeta[]>([]);
  const [companyName, setCompanyName] = useState('Lighthouse Care Services');
  const [companyTagline, setCompanyTagline] = useState('');
  const [companyDescription, setCompanyDescription] = useState('');
  const [companyLogo, setCompanyLogo] = useState<UploadedFileMeta | null>(null);
  const [companyCover, setCompanyCover] = useState<UploadedFileMeta | null>(null);
  type BenefitItem = { id: string; title: string; icon: LucideIcon; selected: boolean; custom?: boolean };
  const [benefits, setBenefits] = useState<BenefitItem[]>([
    { id: 'health', title: 'Health Insurance', icon: Heart, selected: false },
    { id: 'flex', title: 'Flexible Hours', icon: Clock, selected: false },
    { id: 'growth', title: 'Career Growth', icon: TrendingUp, selected: false },
    { id: 'training', title: 'Paid Training', icon: Award, selected: false },
    { id: 'events', title: 'Team Events', icon: Users, selected: false },
    { id: 'pension', title: 'Pension Plan', icon: DollarSign, selected: false },
  ]);
  const [showPreview, setShowPreview] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const extractUploadId = (url?: string) => {
    if (!url) return undefined;
    const match = url.match(/\/uploads\/file\/([^/?#]+)/);
    return match?.[1];
  };
  const { data: brandingData } = useQuery({
    queryKey: ['employer-branding'],
    queryFn: async () => {
      try {
        return await fetchEmployerBranding();
      } catch (err) {
        console.warn('[branding] fetch failed', err);
        return undefined;
      }
    },
  });

  useEffect(() => {
    if (!brandingData) return;
    setCompanyName(brandingData.companyName || '');
    setCompanyTagline(brandingData.tagline || '');
    setCompanyDescription(brandingData.description || '');
    if (brandingData.logoUrl) {
      const logoUploadId = brandingData.logoUploadId ?? extractUploadId(brandingData.logoUrl);
      setCompanyLogo({
        fileUrl: brandingData.logoUrl,
        fileName: 'logo',
        uploadedAt: new Date().toISOString(),
        size: 0,
        mimeType: 'image/*',
        uploadId: logoUploadId,
      });
    }
    if (brandingData.coverUrl) {
      const coverUploadId = brandingData.coverUploadId ?? extractUploadId(brandingData.coverUrl);
      setCompanyCover({
        fileUrl: brandingData.coverUrl,
        fileName: 'cover',
        uploadedAt: new Date().toISOString(),
        size: 0,
        mimeType: 'image/*',
        uploadId: coverUploadId,
      });
    }
    if (brandingData.galleryUrls?.length) {
      setCompanyPhotos(
        brandingData.galleryUrls.map((url, index) => ({
          fileUrl: url,
          fileName: 'gallery',
          uploadedAt: new Date().toISOString(),
          size: 0,
          mimeType: 'image/*',
          uploadId: brandingData.galleryUploadIds?.[index] ?? extractUploadId(url),
        })),
      );
    }
    if (brandingData && Array.isArray(brandingData.benefits) && brandingData.benefits.length) {
      setBenefits((prev) => {
        const benefitTitles = brandingData.benefits ?? [];
        const updated = prev.map((b) => ({ ...b, selected: benefitTitles.includes(b.title) }));
        const existingTitles = new Set(updated.map((b) => b.title));
        const extras = benefitTitles.filter((t) => !existingTitles.has(t));
        if (!extras.length) return updated;
        return [
          ...updated,
          ...extras.map((title) => ({ id: `import-${title}`, title, icon: Plus, selected: true, custom: true })),
        ];
      });
    }
  }, [brandingData]);

  const prevBenefitsTitles = (items: BenefitItem[]) => items.map((b) => b.title);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [notifications, setNotifications] = useState(true);
  const [viewingApplicantsForJob, setViewingApplicantsForJob] = useState<DashboardJob | null>(null);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [composerAttachments, setComposerAttachments] = useState<UploadedFileMeta[]>([]);
  const composerAttachmentsRef = useRef<UploadedFileMeta[]>([]);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [isPartnerTyping, setIsPartnerTyping] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const {
    savedSearches,
    savedSearchesLoading,
    savedSearchesError,
    addSearch: addSavedSearch,
    removeSearch: removeSavedSearch,
    updateCadence: updateSavedSearchCadence,
    defaultCadence: savedSearchDefaultCadence,
  } = useSavedSearches();
  const employerDashboardSummary = useEmployerDashboardSummary();
  const dashboardSummary = employerDashboardSummary.data;
  const conversationsQuery = useConversations();
  const conversationMessagesQuery = useConversationMessages(selectedConversationId ?? undefined);
  const conversationMessages: ConversationMessage[] = conversationMessagesQuery.data ?? [];
  const sendConversationMessageMutation = useSendConversationMessage(selectedConversationId ?? undefined);
  const conversationList = useMemo(() => conversationsQuery.data ?? [], [conversationsQuery.data]);
  const selectedConversation = useMemo(
    () => conversationList.find((conversation) => conversation.id === selectedConversationId),
    [conversationList, selectedConversationId],
  );
  const { logout } = useAuth();

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  useEffect(() => {
    if (!selectedConversationId && conversationList.length) {
      setSelectedConversationId(conversationList[0].id);
    }
  }, [conversationList, selectedConversationId]);

  useConversationEvents(selectedConversationId ?? undefined, (event) => {
    if (event.type === 'typing') {
      setIsPartnerTyping(Boolean(event.active));
      if (event.active) {
        setTimeout(() => setIsPartnerTyping(false), 3000);
      }
    }
  });

  useEffect(() => {
    composerAttachmentsRef.current = composerAttachments;
  }, [composerAttachments]);

  useEffect(() => {
    return () => {
      composerAttachmentsRef.current.forEach((attachment) => {
        if (attachment.uploadId) {
          void deleteUpload(attachment.uploadId).catch((error) => {
            console.warn('[employer] failed to cleanup unsent attachment', {
              uploadId: attachment.uploadId,
              error,
            });
          });
        }
      });
    };
  }, []);

  const formatCurrency = useCallback((value?: number | null) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return null;
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: 0,
    }).format(value);
  }, []);

  const summarisePayPreference = useCallback(
    (search: SavedSearch) => {
      const minLabel = formatCurrency(search.minPay);
      const maxLabel = formatCurrency(search.maxPay);
      if (minLabel && maxLabel) return `Pay: ${minLabel} – ${maxLabel}`;
      if (minLabel) return `Pay: ${minLabel}+`;
      if (maxLabel) return `Pay: up to ${maxLabel}`;
      return '';
    },
    [formatCurrency],
  );

  const describeSavedSearch = useCallback(
    (search: SavedSearch) => {
      const parts = [
        search.query ? `Role: ${search.query}` : null,
        search.location ? `Location: ${search.location}` : null,
        search.jobType && search.jobType !== 'all' ? `Type: ${search.jobType}` : null,
        search.remoteOnly ? 'Remote only' : null,
        search.returnerFriendly ? 'Returner friendly' : null,
        search.tags?.length ? `Tags: ${search.tags.join(', ')}` : null,
      ].filter(Boolean);
      const paySummary = summarisePayPreference(search);
      if (paySummary) {
        parts.push(paySummary);
      }
      return parts.length > 0 ? parts.join(' | ') : 'Any role | Any location';
    },
    [summarisePayPreference],
  );

  const createdLabelForSearch = useCallback((search: SavedSearch) => {
    if (!search.createdAt) return 'Just added';
    const createdDate = new Date(search.createdAt);
    if (Number.isNaN(createdDate.getTime())) {
      return 'Just added';
    }
    return createdDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }, []);

  const handleEmployerSaveSearch = useCallback(() => {
    const defaultName = searchQuery ? `${searchQuery} candidates` : 'New talent search';
    const name = window.prompt('Name this search', defaultName);
    if (!name) return;

    addSavedSearch({
      name: name.trim(),
      query: searchQuery || '',
      location: '',
      jobType: 'all',
      returnerFriendly: false,
      remoteOnly: false,
      tags: [],
      minPay: null,
      maxPay: null,
      cadence: savedSearchDefaultCadence,
    });
    toast.success('Talent search saved');
  }, [addSavedSearch, savedSearchDefaultCadence, searchQuery]);

  const handleViewSavedSearch = useCallback(
    (search: SavedSearch) => {
      toast.info(`Opening matches for "${search.name}"`, {
        action: {
          label: 'Go',
          onClick: () => setActiveTab('talent-pools'),
        },
        duration: 4000,
      });
    },
    [setActiveTab],
  );

  const openConversationForCandidate = useCallback(
    (candidateId: number | string) => {
      const match = conversationList.find((conversation) => conversation.candidateId === String(candidateId));
      if (match) {
        setActiveTab('messages');
        setSelectedConversationId(match.id);
      } else {
        toast.info('Start a conversation from Talent Search to message this candidate.');
      }
    },
    [conversationList],
  );

  const formatRelativeTime = useCallback((iso: string) => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }, []);

  const handleAttachmentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setAttachmentUploading(true);
      const meta = await uploadConversationAttachment(file);
      setComposerAttachments((prev) => [...prev, meta]);
      toast.success('Attachment ready to send.');
    } catch (error) {
      console.error(error);
      toast.error('Attachment upload failed.');
    } finally {
      setAttachmentUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeAttachment = async (id: string) => {
    let removed: UploadedFileMeta | undefined;
    setComposerAttachments((prev) => {
      removed = prev.find((attachment) => attachment.uploadId === id || attachment.fileUrl === id);
      return prev.filter((attachment) => attachment.uploadId !== id && attachment.fileUrl !== id);
    });
    if (removed?.uploadId) {
      try {
        await deleteUpload(removed.uploadId);
      } catch (error) {
        console.error(error);
        toast.error('Attachment removed from the thread, but storage cleanup failed.');
      }
    }
  };

  const handleSendMessage = async () => {
    if (!selectedConversationId) {
      toast.error('Select a conversation first.');
      return;
    }
    if (!messageText.trim() && composerAttachments.length === 0) {
      toast.info('Type a message or attach a file.');
      return;
    }
    try {
      await sendConversationMessageMutation.mutateAsync({ body: messageText.trim() || 'Attachment', attachments: composerAttachments });
      setMessageText('');
      setComposerAttachments([]);
    } catch (error) {
      console.error(error);
      toast.error('Unable to send message.');
    }
  };

  // Get candidates data
  const _legacyMockCandidates = ([] as any[]).map((candidate) => ({
    id: candidate.id,
    name: candidate.name,
    title: candidate.title || 'Job Seeker',
    location: candidate.location,
    experience: candidate.experience || 'Not specified',
    aiScore: Math.floor(Math.random() * 20) + 80,
    skills: candidate.skills || [],
    availability: 'Immediate',
    salary: '£24,000 - £32,000',
    summary: candidate.bio || 'Experienced professional seeking new opportunities.',
    matchReasons: [
      `Location: ${candidate.location}`,
      `Experience: ${candidate.experience || 'Multiple years'}`,
      'Skills match job requirements',
      'Available for interview'
    ]
  }));

  const getMockApplicantsForJob = (job: DashboardJob): EmployerApplicant[] =>
    candidateMatches.slice(0, 4).map((candidate) => ({
      id: String(candidate.id),
      candidateId: String(candidate.id),
      jobId: String(job.id),
      jobTitle: job.title,
      name: candidate.name,
      title: candidate.title,
      location: candidate.location,
      experience: candidate.experience ?? 'Not specified',
      aiScore: candidate.aiScore,
      skills: candidate.skills,
      availability: 'Immediate',
      salary: 'GBP 24,000 - 32,000',
      summary: candidate.summary,
      matchReasons: candidate.matchReasons,
      appliedDate: new Date().toISOString(),
      email: undefined,
      resumeUrl: undefined,
      status: 'new',
    }));

  const computeConversionRate = (job: { conversionRate?: number; views?: number; applicants?: number }) => {
    if (typeof job.conversionRate === 'number' && !Number.isNaN(job.conversionRate)) {
      return Number(job.conversionRate.toFixed(1));
    }
    const views = typeof job.views === 'number' ? job.views : 0;
    const applicants = typeof job.applicants === 'number' ? job.applicants : 0;
    if (views > 0) {
      return Number(((applicants / views) * 100).toFixed(1));
    }
    if (applicants > 0) {
      return 100;
    }
    return 0;
  };

  type DashboardJob = {
    id: number | string;
    title: string;
    location: string;
    salary: string;
    type: string;
    posted: string;
    applicants: number;
    views: number;
    status: string;
    conversionRate: number;
    occupationId?: string;
    occupationTitle?: string;
  };

  const toDashboardJob = useCallback(
    (job: EmployerJob | DashboardJob) => ({
      id: job.id,
      title: job.title,
      location: job.location,
      salary: job.salary,
      type: job.type,
      posted: job.posted,
      applicants: job.applicants,
      views: job.views ?? 0,
      status: job.status,
      conversionRate: computeConversionRate(job),
      occupationId: (job as { occupationId?: string }).occupationId,
      occupationTitle: (job as { occupationTitle?: string }).occupationTitle,
    }),
    [],
  );

  const [jobPosts, setJobPosts] = useState<DashboardJob[]>([]);
  const [analyticsOccupationQuery, setAnalyticsOccupationQuery] = useState('');
  const [analyticsOccupationId, setAnalyticsOccupationId] = useState<string | undefined>(undefined);
  const occupationSearchAnalytics = useOccupationSearch(analyticsOccupationQuery);
  const analyticsRoleSuggestions = useMemo(
    () => occupationSearchAnalytics.data?.slice(0, 6) ?? [],
    [occupationSearchAnalytics.data],
  );

  const employerJobsQuery = useEmployerJobs({ status: 'all' });
  const employerJobs = useMemo(() => employerJobsQuery.data ?? [], [employerJobsQuery.data]);
  const selectedJobId = viewingApplicantsForJob ? String(viewingApplicantsForJob.id) : undefined;
  const employerApplicantsQuery = useEmployerApplicants();
  const applicantsByJobId = useMemo(() => {
    const grouped = new Map<string, EmployerApplicant[]>();
    (employerApplicantsQuery.data ?? []).forEach((applicant) => {
      const key = applicant.jobId?.toString?.() ?? '';
      if (!key) return;
      const existing = grouped.get(key) ?? [];
      existing.push(applicant);
      grouped.set(key, existing);
    });
    return grouped;
  }, [employerApplicantsQuery.data]);
  useEffect(() => {
    if (employerJobs.length) {
      setJobPosts(employerJobs.map((job) => toDashboardJob(job)));
    }
  }, [employerJobs, toDashboardJob]);

  const filteredJobPosts = useMemo(() => {
    const q = analyticsOccupationQuery.trim().toLowerCase();
    return jobPosts.filter((job) => {
      if (analyticsOccupationId && job.occupationId !== analyticsOccupationId) return false;
      if (q) return false; // server filters, no local search
      return true;
    });
  }, [analyticsOccupationId, analyticsOccupationQuery, jobPosts]);

  type AnalyticsDisplayRow = {
    jobTitle: string;
    views: number;
    applications: number;
    conversionRate: number;
    avgTimeToApply: string;
    topSource: string;
  };

  const apiJobAnalytics = useQuery<JobAnalyticsRow[]>({
    queryKey: ['job-analytics', analyticsOccupationId],
    queryFn: () => fetchJobAnalytics(analyticsOccupationId),
    enabled: true,
    staleTime: 1000 * 30,
  });

  const analyticsDisplayRows: AnalyticsDisplayRow[] = useMemo(() => {
    if (apiJobAnalytics.data?.length) {
      return apiJobAnalytics.data.map((row) => {
        const views = row.views ?? 0;
        const applies = row.applies ?? 0;
        const conversionRate = views > 0 ? Number(((applies / views) * 100).toFixed(1)) : applies > 0 ? 100 : 0;
        return {
          jobTitle: row.title ?? 'Job',
          views,
          applications: applies,
          conversionRate,
          avgTimeToApply: '--',
          topSource: 'Job Alerts',
        };
      });
    }
    return [];
  }, [apiJobAnalytics.data]);

  const analyticsSummary = useMemo(() => {
    const rows = analyticsDisplayRows;
    const totalViews = rows.reduce((sum: number, row: AnalyticsDisplayRow) => sum + (row.views ?? 0), 0);
    const totalApplications = rows.reduce((sum: number, row: AnalyticsDisplayRow) => sum + (row.applications ?? 0), 0);
    const avgConversion =
      rows.length > 0
        ? Number((rows.reduce((sum: number, row: AnalyticsDisplayRow) => sum + (row.conversionRate ?? 0), 0) / rows.length).toFixed(1))
        : 0;
    const engagement =
      totalViews > 0 ? Math.min(100, Math.round((totalApplications / totalViews) * 100)) : totalApplications > 0 ? 100 : 0;
    return {
      totalViews,
      totalApplications,
      avgConversion,
      engagement,
    };
  }, [analyticsDisplayRows]);

  const talentSearchPreview = useTalentSearch({ returnerFriendly: true });
  const candidateMatches =
    (talentSearchPreview.data ?? []).map((candidate) => ({
      id: candidate.id,
      name: candidate.name,
      title: candidate.title || 'Job Seeker',
      location: candidate.location,
      experience:
        typeof candidate.experienceYears === 'number'
          ? `${candidate.experienceYears} years`
          : 'Not specified',
      aiScore: candidate.matchScore ?? 80,
      skills: candidate.skills ?? [],
      availability: candidate.availability ?? 'Immediate',
      salary: candidate.salaryRange ?? '£24,000 - £32,000',
      summary: candidate.bio ?? 'Experienced professional seeking new opportunities.',
      matchReasons: [
        `Location: ${candidate.location}`,
        `Experience: ${candidate.experienceYears ?? 'Multiple years'}`,
      ],
    })) as {
      id: string | number;
      name: string;
      title: string;
      location: string;
      experience: string;
      aiScore: number;
      skills: string[];
      availability: string;
      salary: string;
      summary: string;
      matchReasons: string[];
    }[];
  const talentPools = useMemo(
    () => buildTalentPools(talentSearchPreview.data ?? []),
    [talentSearchPreview.data],
  );
  const isAnalyticsLoading = employerDashboardSummary.isPending || employerJobsQuery.isPending;
  const isTalentLoading = talentSearchPreview.isPending;

  const kpiIconMap: Record<string, React.ElementType> = {
    'active-jobs': Briefcase,
    applicants: Users,
    shortlisted: Star,
    messages: MessageSquare,
  };

  const insightIconMap: Record<string, React.ElementType> = {
    'trending-up': TrendingUpIcon,
    target: Target,
    flame: Flame,
  };

  const shortlistKey = (value: number | string) => value.toString();

  const toggleShortlist = (id: number | string) => {
    const key = shortlistKey(id);
    const previousSnapshot = [...shortlistedIds];
    const wasShortlisted = previousSnapshot.includes(key);
    const updated = wasShortlisted
      ? previousSnapshot.filter((candidateId) => candidateId !== key)
      : [...previousSnapshot, key];

    setShortlistedIds(updated);

    toast.success(wasShortlisted ? 'Removed from shortlist' : 'Added to shortlist', {
      duration: 4000,
      action: {
        label: 'Undo',
        onClick: () => setShortlistedIds(previousSnapshot),
      },
    });
  };

  const sendMessage = () => {
    if (messageText.trim()) {
      // In production, this would send via backend
      toast.success('Message sent successfully!');
      setMessageText('');
    }
  };

  // Export analytics data to CSV
  const exportAnalytics = () => {
    toast.loading('Preparing analytics export...');
    
    setTimeout(() => {
      const headers = ['Job Title', 'Views', 'Applications', 'Conversion Rate', 'Avg Time to Apply', 'Top Source'];
      const rows = analyticsDisplayRows.map(job => [
        job.jobTitle,
        job.views,
        job.applications,
        `${job.conversionRate}%`,
        job.avgTimeToApply,
        job.topSource
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lighthouse-job-analytics-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success('Analytics data exported successfully!', {
        description: `Downloaded ${analyticsDisplayRows.length} job performance reports`,
      });
    }, 1000);
  };

  const resolveGalleryUploadIds = (photos: UploadedFileMeta[]) => {
    if (!photos.length) return undefined;
    const ids = photos.map((photo) => photo.uploadId ?? extractUploadId(photo.fileUrl ?? photo.publicUrl));
    if (ids.some((id) => !id)) return undefined;
    return ids as string[];
  };

  const buildBrandingPayload = (
    overrides: Partial<EmployerBrandingPayload> = {},
    photosOverride?: UploadedFileMeta[],
  ): EmployerBrandingPayload => {
    const selectedBenefits = benefits.filter((b) => b.selected).map((b) => b.title);
    const gallerySource = photosOverride ?? companyPhotos;
    const basePayload: EmployerBrandingPayload = {
      companyName: companyName || 'Company',
      tagline: companyTagline || undefined,
      description: companyDescription || undefined,
      logoUrl: companyLogo?.fileUrl ?? companyLogo?.publicUrl,
      logoUploadId: companyLogo?.uploadId ?? extractUploadId(companyLogo?.fileUrl ?? companyLogo?.publicUrl),
      coverUrl: companyCover?.fileUrl ?? companyCover?.publicUrl,
      coverUploadId: companyCover?.uploadId ?? extractUploadId(companyCover?.fileUrl ?? companyCover?.publicUrl),
      galleryUrls: gallerySource.map((photo) => photo.fileUrl ?? photo.publicUrl),
      galleryUploadIds: resolveGalleryUploadIds(gallerySource),
      benefits: selectedBenefits,
    };
    return { ...basePayload, ...overrides };
  };

  const deleteUploadsById = async (targets: Array<UploadedFileMeta | null | undefined>) => {
    const deletions = targets
      .filter((item): item is UploadedFileMeta => Boolean(item?.uploadId))
      .map((item) => deleteUpload(item.uploadId!).catch(() => undefined));
    if (deletions.length) {
      await Promise.all(deletions);
    }
  };

  // Handle photo upload
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setGalleryUploading(true);
    const previousSnapshot = [...companyPhotos];
    const newUploads: UploadedFileMeta[] = [];
    try {
      for (const file of Array.from(files)) {
        const uploaded = await uploadCompanyAsset('company-gallery', file);
        newUploads.push(uploaded);
      }
      const nextPhotos = [...previousSnapshot, ...newUploads];
      setCompanyPhotos(nextPhotos);
      await saveEmployerBranding(buildBrandingPayload({}, nextPhotos));
      toast.success('Gallery updated', {
        description: `${files.length} file(s) uploaded`,
        action: {
          label: 'Undo',
          onClick: () => {
            setCompanyPhotos(previousSnapshot);
            void deleteUploadsById(newUploads);
            void saveEmployerBranding(buildBrandingPayload({}, previousSnapshot)).catch((error) => {
              const message = error instanceof Error ? error.message : 'Unable to restore gallery';
              toast.error(message);
            });
          },
        },
      });
    } catch (err) {
      setCompanyPhotos(previousSnapshot);
      await deleteUploadsById(newUploads);
      const message = err instanceof Error ? err.message : 'Upload failed';
      toast.error(message);
    } finally {
      setGalleryUploading(false);
      e.target.value = '';
    }
  };

  const handleBrandAssetUpload = async (file: File, kind: 'logo' | 'cover') => {
    const previousLogo = companyLogo;
    const previousCover = companyCover;
    let uploaded: UploadedFileMeta | undefined;
    if (kind === 'logo') {
      setLogoUploading(true);
    } else {
      setCoverUploading(true);
    }
    try {
      uploaded = await uploadCompanyAsset(kind === 'logo' ? 'logo' : 'company-cover', file);
      if (kind === 'logo') {
        setCompanyLogo(uploaded);
      } else {
        setCompanyCover(uploaded);
      }
      const nextLogo = kind === 'logo' ? uploaded : companyLogo;
      const nextCover = kind === 'cover' ? uploaded : companyCover;
      await saveEmployerBranding(
        buildBrandingPayload({
          logoUrl: nextLogo?.fileUrl ?? nextLogo?.publicUrl,
          logoUploadId: nextLogo?.uploadId ?? extractUploadId(nextLogo?.fileUrl ?? nextLogo?.publicUrl),
          coverUrl: nextCover?.fileUrl ?? nextCover?.publicUrl,
          coverUploadId: nextCover?.uploadId ?? extractUploadId(nextCover?.fileUrl ?? nextCover?.publicUrl),
        }),
      );
      if (kind === 'logo' && previousLogo?.uploadId) {
        await deleteUpload(previousLogo.uploadId);
      }
      if (kind === 'cover' && previousCover?.uploadId) {
        await deleteUpload(previousCover.uploadId);
      }
      toast.success(kind === 'logo' ? 'Logo saved' : 'Cover saved');
    } catch (err) {
      if (uploaded?.uploadId) {
        await deleteUpload(uploaded.uploadId).catch(() => undefined);
      }
      if (kind === 'logo') {
        setCompanyLogo(previousLogo ?? null);
      } else {
        setCompanyCover(previousCover ?? null);
      }
      const message = err instanceof Error ? err.message : 'Upload failed';
      toast.error(message);
    } finally {
      if (kind === 'logo') {
        setLogoUploading(false);
      } else {
        setCoverUploading(false);
      }
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    void handleBrandAssetUpload(file, 'logo');
    e.target.value = '';
  };

  const handleCoverUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    void handleBrandAssetUpload(file, 'cover');
    e.target.value = '';
  };

  const toggleBenefit = (id: string) => {
    setBenefits((prev) =>
      prev.map((b) => (b.id === id ? { ...b, selected: !b.selected } : b)),
    );
  };

  const addCustomBenefit = () => {
    const title = window.prompt('Add a custom benefit');
    if (!title || !title.trim()) return;
    const id = `custom-${Date.now()}`;
    setBenefits((prev) => [...prev, { id, title: title.trim(), icon: Plus, selected: true, custom: true }]);
    toast.success('Custom benefit added');
  };

  const handleSavePublish = async () => {
    const payload = buildBrandingPayload();
    const selectedBenefits = payload.benefits ?? [];
    try {
      await saveEmployerBranding(payload);
      toast.success('Branding saved & published', {
        description: `${companyName || 'Company'} · ${selectedBenefits.length} benefits · ${companyPhotos.length} gallery items`,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to save branding';
      toast.error(message);
    }
  };

  const handlePreviewProfile = () => {
    setShowPreview(true);
  };

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
  };

  const handleDeleteJob = (jobId: string | number) => {
    const previousSnapshot = [...jobPosts];
    const index = jobPosts.findIndex((job) => String(job.id) === String(jobId));
    if (index === -1) {
      return;
    }
    setJobPosts((prev) => prev.filter((job) => String(job.id) !== String(jobId)));
    toast.success('Job removed', {
      duration: 4000,
      action: {
        label: 'Undo',
        onClick: () => setJobPosts(previousSnapshot),
      },
    });
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null) return;

    const previousOrder = [...companyPhotos];
    const newPhotos = [...companyPhotos];
    const draggedPhoto = newPhotos[draggedIndex];
    newPhotos.splice(draggedIndex, 1);
    newPhotos.splice(dropIndex, 0, draggedPhoto);
    
    setCompanyPhotos(newPhotos);
    setDraggedIndex(null);
    try {
      await saveEmployerBranding(buildBrandingPayload({}, newPhotos));
      toast.success('Photo reordered!', {
        duration: 4000,
        action: {
          label: 'Undo',
          onClick: () => {
            setCompanyPhotos(previousOrder);
            void saveEmployerBranding(buildBrandingPayload({}, previousOrder)).catch((error) => {
              const message = error instanceof Error ? error.message : 'Unable to restore photo order';
              toast.error(message);
            });
          },
        },
      });
    } catch (error) {
      setCompanyPhotos(previousOrder);
      const message = error instanceof Error ? error.message : 'Unable to save photo order';
      toast.error(message);
    }
  };

  const removePhoto = async (index: number) => {
    const previousSnapshot = [...companyPhotos];
    const target = previousSnapshot[index];
    if (!target) {
      return;
    }
    const nextPhotos = previousSnapshot.filter((_, i) => i !== index);
    setCompanyPhotos(nextPhotos);
    try {
      await saveEmployerBranding(buildBrandingPayload({}, nextPhotos));
      if (target.uploadId) {
        await deleteUpload(target.uploadId);
      }
      toast.success('Photo removed!', {
        duration: 4000,
      });
    } catch (error) {
      setCompanyPhotos(previousSnapshot);
      const message = error instanceof Error ? error.message : 'Unable to remove photo';
      toast.error(message);
    }
  };

  // Real-time notifications simulation
  useEffect(() => {
    if (!notifications || savedSearches.length === 0) {
      return;
    }

    const notificationInterval = setInterval(() => {
      const randomSearch = savedSearches[Math.floor(Math.random() * savedSearches.length)];
      toast.info(`New candidate matches your search: "${randomSearch?.name ?? 'Saved search'}"`, {
        action: {
          label: 'View',
          onClick: () => setActiveTab('talent-pools'),
        },
        duration: 5000,
      });
    }, 30000);

    return () => clearInterval(notificationInterval);
  }, [notifications, savedSearches]);

  return (
    <main
      className="relative min-h-screen overflow-hidden bg-[radial-gradient(circle_at_6%_10%,rgba(99,102,241,0.18),transparent_52%),linear-gradient(180deg,#030712_0%,#020816_70%,#01020a_100%)]"
      aria-labelledby="employer-dashboard-title"
    >
      <Helmet>
        <title>Employer Dashboard | Lighthouse Jobs</title>
        <meta
          name="description"
          content="Track jobs, applicants, talent pools, and analytics from your Lighthouse employer workspace."
        />
      </Helmet>
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[rgba(5,9,19,0.8)] backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gold-gradient flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-black" />
              </div>
              <span className="text-gold-gradient">
                Lighthouse Jobs
              </span>
            </Link>
            
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" className="relative text-white">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#ba9036] rounded-full" />
              </Button>
              <Link to="/employer/search">
                <Button className="bg-gold-gradient text-black hover:brightness-110">
                  Talent Search
                </Button>
              </Link>
              <Link to="/employer/settings">
                <Button variant="ghost" size="icon" className="text-white">
                  <Settings className="w-5 h-5" />
                </Button>
              </Link>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-white"
                onClick={handleLogout}
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 space-y-2">
          <RoleBadge role="employer" />
          <h1 id="employer-dashboard-title" className="mb-2 text-gold-gradient">
            Employer Dashboard
          </h1>
          <p className="text-gray-400">Manage your jobs and find top talent</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} aria-label="Employer dashboard sections">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
            <aside
              aria-label="Employer dashboard navigation"
              className="lg:sticky lg:top-24 lg:h-max lg:w-56"
            >
              <TabsList className="relative flex h-full w-full flex-col gap-1.5 rounded-2xl border border-white/10 bg-[#050913]/80 p-3 shadow-[0_25px_65px_-45px_rgba(3,7,18,0.85)]">
                <TabsTrigger
                  value="overview"
                  className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-gray-200 transition data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#f7ef8a]/25 data-[state=active]:via-[#edc967]/20 data-[state=active]:to-[#d2ac47]/25 data-[state=active]:text-[#f7ef8a]"
                >
                  <TrendingUp className="h-4 w-4" aria-hidden />
                  <span>Overview</span>
                </TabsTrigger>
                <TabsTrigger
                  value="applicants"
                  className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-gray-200 transition data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#f7ef8a]/25 data-[state=active]:via-[#edc967]/20 data-[state=active]:to-[#d2ac47]/25 data-[state=active]:text-[#f7ef8a]"
                >
                  <Users className="h-4 w-4" aria-hidden />
                  <span>Applicants</span>
                </TabsTrigger>
                <TabsTrigger
                  value="candidates"
                  className="flex items-center gap-2 rounded-xl px-4 py-3 text-left text-sm font-medium text-gray-200 transition data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#f7ef8a]/25 data-[state=active]:via-[#edc967]/20 data-[state=active]:to-[#d2ac47]/25 data-[state=active]:text-[#f7ef8a]"
                >
                  <Users className="h-4 w-4" aria-hidden />
                  <span>Find Candidates</span>
                </TabsTrigger>
                <TabsTrigger
                  value="talent-pools"
                  className="flex items-center gap-2 rounded-xl px-4 py-3 text-left text-sm font-medium text-gray-200 transition data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#f7ef8a]/25 data-[state=active]:via-[#edc967]/20 data-[state=active]:to-[#d2ac47]/25 data-[state=active]:text-[#f7ef8a]"
                >
                  <Database className="h-4 w-4" aria-hidden />
                  <span>Talent Pools</span>
                </TabsTrigger>
                <TabsTrigger
                  value="jobs"
                  className="flex items-center gap-2 rounded-xl px-4 py-3 text-left text-sm font-medium text-gray-200 transition data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#f7ef8a]/25 data-[state=active]:via-[#edc967]/20 data-[state=active]:to-[#d2ac47]/25 data-[state=active]:text-[#f7ef8a]"
                >
                  <Briefcase className="h-4 w-4" aria-hidden />
                  <span>My Jobs</span>
                </TabsTrigger>
                <TabsTrigger
                  value="shortlist"
                  className="flex items-center gap-2 rounded-xl px-4 py-3 text-left text-sm font-medium text-gray-200 transition data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#f7ef8a]/25 data-[state=active]:via-[#edc967]/20 data-[state=active]:to-[#d2ac47]/25 data-[state=active]:text-[#f7ef8a]"
                >
                  <Star className="h-4 w-4" aria-hidden />
                  <span>Shortlist</span>
                </TabsTrigger>
                <TabsTrigger
                  value="analytics"
                  className="flex items-center gap-2 rounded-xl px-4 py-3 text-left text-sm font-medium text-gray-200 transition data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#f7ef8a]/25 data-[state=active]:via-[#edc967]/20 data-[state=active]:to-[#d2ac47]/25 data-[state=active]:text-[#f7ef8a]"
                >
                  <BarChart3 className="h-4 w-4" aria-hidden />
                  <span>Analytics</span>
                </TabsTrigger>
                <TabsTrigger
                  value="skill-tests"
                  className="flex items-center gap-2 rounded-xl px-4 py-3 text-left text-sm font-medium text-gray-200 transition data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#f7ef8a]/25 data-[state=active]:via-[#edc967]/20 data-[state=active]:to-[#d2ac47]/25 data-[state=active]:text-[#f7ef8a]"
                >
                  <Award className="h-4 w-4" aria-hidden />
                  <span>Skill Tests</span>
                </TabsTrigger>
                <TabsTrigger
                  value="branding"
                  className="flex items-center gap-2 rounded-xl px-4 py-3 text-left text-sm font-medium text-gray-200 transition data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#f7ef8a]/25 data-[state=active]:via-[#edc967]/20 data-[state=active]:to-[#d2ac47]/25 data-[state=active]:text-[#f7ef8a]"
                >
                  <Palette className="h-4 w-4" aria-hidden />
                  <span>Branding</span>
                </TabsTrigger>
                <TabsTrigger
                  value="messages"
                  className="flex items-center gap-2 rounded-xl px-4 py-3 text-left text-sm font-medium text-gray-200 transition data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#f7ef8a]/25 data-[state=active]:via-[#edc967]/20 data-[state=active]:to-[#d2ac47]/25 data-[state=active]:text-[#f7ef8a]"
                >
                  <MessageSquare className="h-4 w-4" aria-hidden />
                  <span>Messages</span>
                </TabsTrigger>
              </TabsList>
            </aside>

          <div className="flex-1">
          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              {employerDashboardSummary.isPending && !dashboardSummary ? (
                Array.from({ length: 4 }).map((_, idx) => (
                  <div key={`kpi-skeleton-${idx}`} className="animate-pulse rounded-2xl border border-white/10 bg-white/5 p-6">
                    <div className="mb-4 h-12 w-12 rounded-lg bg-white/10" />
                    <div className="mb-2 h-6 w-16 rounded bg-white/10" />
                    <div className="mb-2 h-4 w-24 rounded bg-white/10" />
                    <div className="h-3 w-20 rounded bg-white/5" />
                  </div>
                ))
              ) : (
                (dashboardSummary?.kpis ?? []).map((stat) => {
                  const Icon = (stat.id && kpiIconMap[stat.id]) || TrendingUp;
                  const clickable = Boolean(stat.targetTab);
                  return (
                    <Card
                      key={stat.id}
                      className={`bg-white/5 border-white/10 transition-all duration-300 ${
                        clickable
                          ? 'cursor-pointer hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)]'
                          : 'cursor-default'
                      }`}
                      onClick={() => {
                        if (stat.targetTab) {
                          setActiveTab(stat.targetTab);
                        }
                      }}
                    >
                      <CardContent className="p-6">
                        <div className="mb-4 flex items-start justify-between">
                          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gold-gradient">
                            <Icon className="h-6 w-6 text-black" aria-hidden />
                          </div>
                        </div>
                        <div className="text-white">{stat.value}</div>
                        <p className="text-sm text-gray-400">{stat.label}</p>
                        <p className="text-xs text-[#ba9036]">{stat.trend}</p>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
            {employerDashboardSummary.isError && (
              <p className="text-xs text-red-300">Unable to refresh KPI data. Showing cached values.</p>
            )}

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              {(dashboardSummary?.insights ?? []).map((insight) => {
                const Icon = insight.icon ? insightIconMap[insight.icon] ?? Sparkles : Sparkles;
                return (
                  <Card key={insight.id} className="role-surface-employer bg-white/5 border-white/10 overflow-hidden">
                    <CardContent className="space-y-4 p-6">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs uppercase tracking-[0.3em] text-gray-500">Insight</p>
                          <CardTitle className="mt-1 text-white">{insight.title}</CardTitle>
                        </div>
                        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gold-gradient text-black">
                          <Icon className="h-5 w-5" aria-hidden />
                        </span>
                      </div>
                      <div>
                        <p className="text-3xl font-semibold text-gold-gradient">{insight.value}</p>
                        <p className="mt-2 text-sm text-gray-300">{insight.description}</p>
                      </div>
                      <ul className="space-y-2 text-sm text-gray-400">
                        {insight.highlights.map((item, highlightIndex) => (
                          <li key={highlightIndex} className="flex items-center gap-2">
                            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-[#d2ac47]" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                      {insight.actionLabel && insight.actionLink && (
                        <Button asChild variant="outline" className="border-[#d2ac47]/50 text-[#ba9036] hover:bg-[#ba9036]/10">
                          <Link to={insight.actionLink}>{insight.actionLabel}</Link>
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="role-surface-employer bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-[#ba9036]" />
                    Smart Recommendations
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Top candidates matching your requirements
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {candidateMatches.length === 0 && !talentSearchPreview.isPending && (
                    <p className="text-sm text-gray-400">No candidates available yet. Try broadening your talent search filters.</p>
                  )}
                  {candidateMatches.slice(0, 2).map((candidate) => (
                    <div
                      key={candidate.id}
                      className="p-4 bg-white/5 rounded-lg border border-white/10 hover:border-[#d2ac47]/50 transition-all cursor-pointer"
                      onClick={() => {
                        setSelectedCandidate(candidate);
                        setActiveTab('candidates');
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <Avatar>
                          <AvatarFallback className="bg-gradient-to-br from-[#ae8625] to-[#f7ef8a] text-black">
                            {candidate.name
                              .split(' ')
                              .map((n: string) => n[0] ?? '')
                              .join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-white truncate">{candidate.name}</p>
                            <Badge className="bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50">
                              {candidate.aiScore}% Match
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-400">{candidate.title}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Award className="w-5 h-5 text-[#ba9036]" />
                    Quick Actions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button
                    asChild
                    className="w-full justify-start bg-gold-gradient hover:brightness-110 text-black"
                  >
                    <Link to="/employer/job/new">
                      <Plus className="w-4 h-4 mr-2" />
                      Post New Job
                    </Link>
                  </Button>
                  
                  <Button
                    variant="outline"
                    className="w-full justify-start border-white/20 text-white hover:bg-white/5"
                    onClick={() => setActiveTab('candidates')}
                  >
                    <Search className="w-4 h-4 mr-2" />
                    Search Candidates
                  </Button>
                  
                  <Button
                    variant="outline"
                    className="w-full justify-start border-white/20 text-white hover:bg-white/5"
                    onClick={() => setActiveTab('analytics')}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    View Analytics
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Quick View: Talent Pools */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Database className="w-5 h-5 text-[#ba9036]" />
                      Talent Pools Overview
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      Your saved candidate pools
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-white/20 text-white hover:bg-white/5"
                    onClick={() => setActiveTab('talent-pools')}
                  >
                    View All
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  {isTalentLoading && (
                    <>
                      {Array.from({ length: 3 }).map((_, idx) => (
                        <SkeletonCard key={idx} className="h-32" />
                      ))}
                    </>
                  )}
                  {talentPools.length === 0 && !isTalentLoading && (
                    <p className="col-span-3 text-sm text-gray-400">No talent pools yet. Save candidates to create pools.</p>
                  )}
                  {!isTalentLoading &&
                    talentPools.slice(0, 3).map((pool) => (
                    <Card
                      key={pool.id}
                      className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 transition-all cursor-pointer"
                      onClick={() => setActiveTab('talent-pools')}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <p className="text-white mb-1">{pool.name}</p>
                            <p className="text-xs text-gray-400">Updated {pool.lastUpdated}</p>
                          </div>
                          <Badge className="bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50 ml-2">
                            {pool.matchRate}%
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-sm text-gray-300">
                            <Users className="w-4 h-4" />
                            <span>{pool.candidateCount} candidates</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    ))}
                </div>
              </CardContent>
            </Card>

            {/* Quick View: Posted Jobs */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-[#ba9036]" />
                      Your Posted Jobs
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      Active job listings and performance
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-white/20 text-white hover:bg-white/5"
                    onClick={() => setActiveTab('jobs')}
                  >
                    View All
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {jobPosts.map((job) => (
                    <Card
                      key={job.id}
                      className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 transition-all"
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-white">{job.title}</p>
                              <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                                {job.status}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-400">
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {job.location}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {job.posted}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-6 text-sm">
                          <div className="flex items-center gap-2 text-[#ba9036]">
                            <Eye className="w-4 h-4" />
                            <span>{job.views} views</span>
                          </div>
                          <div className="flex items-center gap-2 text-green-400">
                            <Users className="w-4 h-4" />
                            <span>{job.applicants} applicants</span>
                          </div>
                          <div className="text-gray-400">
                            {job.salary}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Quick View: Analytics Summary */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-[#ba9036]" />
                      Analytics Summary
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      Job performance metrics
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-white/20 text-white hover:bg-white/5"
                    onClick={() => setActiveTab('analytics')}
                  >
                    View Details
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {isAnalyticsLoading ? (
                  <div className="grid md:grid-cols-2 gap-6">
                    {Array.from({ length: 2 }).map((_, idx) => (
                      <SkeletonCard key={idx} className="h-40" />
                    ))}
                  </div>
                ) : analyticsDisplayRows.length === 0 ? (
                  <p className="text-sm text-gray-400">No analytics yet. Post a job to start collecting performance data.</p>
                ) : (
                  <div className="grid md:grid-cols-2 gap-6">
                    {analyticsDisplayRows.map((job, index) => (
                      <div key={index} className="space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="text-white">{job.jobTitle}</p>
                          <Badge variant="outline" className="border-white/20 text-gray-300">
                            {job.topSource}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-white/5 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-1">
                              <Eye className="w-4 h-4 text-blue-400" />
                              <p className="text-xs text-gray-400">Views</p>
                            </div>
                            <p className="text-white">{job.views}</p>
                          </div>
                          <div className="bg-white/5 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-1">
                              <Users className="w-4 h-4 text-green-400" />
                              <p className="text-xs text-gray-400">Applications</p>
                            </div>
                            <p className="text-white">{job.applications}</p>
                          </div>
                          <div className="bg-white/5 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-1">
                              <TrendingUp className="w-4 h-4 text-[#ba9036]" />
                              <p className="text-xs text-gray-400">Conversion</p>
                            </div>
                            <p className="text-white">{job.conversionRate}%</p>
                          </div>
                          <div className="bg-white/5 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-1">
                              <Clock className="w-4 h-4 text-purple-400" />
                              <p className="text-xs text-gray-400">Avg. Time</p>
                            </div>
                            <p className="text-white">{job.avgTimeToApply}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Applicants Tab */}
          <TabsContent value="applicants" className="space-y-6">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#ba9036]" />
                  Job Applicants
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Review candidates who applied to your job postings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {jobPosts.map((job) => {
                  const jobKey = job.id.toString();
                  const resolvedApplicants = applicantsByJobId.get(jobKey) ?? [];
                  const showEmptyState = !resolvedApplicants.length && !employerApplicantsQuery.isPending;

                  return (
                    <Card key={job.id} className="bg-white/5 border-white/10">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-white mb-2">{job.title}</CardTitle>
                            <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {job.location}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                Posted {job.posted}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {resolvedApplicants.length} Applicants
                              </span>
                            </div>
                          </div>
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                            {job.status}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {showEmptyState ? (
                          <div className="text-center py-8">
                            <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                            <p className="text-gray-400">No applicants yet</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {resolvedApplicants.map((applicant) => (
                              <Card
                                key={applicant.id}
                                className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)] transition-all duration-300"
                              >
                                <CardContent className="p-4">
                                  <div className="flex items-start gap-4">
                                    <Avatar className="w-12 h-12">
                                      <AvatarFallback className="bg-gradient-to-br from-[#ae8625] to-[#f7ef8a] text-black">
                                        {applicant.name
                                          .split(' ')
                                          .map((n: string) => n[0] ?? '')
                                          .join('')}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-start justify-between mb-2">
                                        <div>
                                          <div className="flex items-center gap-2 mb-1">
                                            <h4 className="text-white">{applicant.name}</h4>
                                            <Badge className="bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50">
                                              <Award className="w-3 h-3 mr-1" />
                                              {applicant.aiScore}% Match
                                            </Badge>
                                          </div>
                                          <p className="text-sm text-gray-300 mb-2">{applicant.title}</p>
                                          <div className="flex flex-wrap gap-3 text-xs text-gray-400">
                                            <span className="flex items-center gap-1">
                                              <MapPin className="w-3 h-3" />
                                              {applicant.location}
                                            </span>
                                            <span className="flex items-center gap-1">
                                              <Clock className="w-3 h-3" />
                                              {applicant.experience}
                                            </span>
                                            <span className="flex items-center gap-1">
                                              <Calendar className="w-3 h-3" />
                                              Applied 2 days ago
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      <p className="text-sm text-gray-300 mb-3 line-clamp-2">{applicant.summary}</p>

                                      <div className="flex flex-wrap gap-2 mb-3">
                                        {applicant.skills.slice(0, 4).map((skill, idx) => (
                                          <Badge
                                            key={idx}
                                            variant="outline"
                                            className="border-white/20 text-gray-300 text-xs"
                                          >
                                            {skill}
                                          </Badge>
                                        ))}
                                        {applicant.skills.length > 4 && (
                                          <Badge
                                            variant="outline"
                                            className="border-white/20 text-gray-300 text-xs"
                                          >
                                            +{applicant.skills.length - 4} more
                                          </Badge>
                                        )}
                                      </div>

                                      <div className="flex flex-wrap gap-2">
                                        <Button
                                          onClick={() => toggleShortlist(applicant.id)}
                                          variant={shortlistedIds.includes(shortlistKey(applicant.id)) ? "default" : "outline"}
                                          size="sm"
                                          className={
                                            shortlistedIds.includes(shortlistKey(applicant.id))
                                              ? "bg-gold-gradient text-black"
                                              : "border-white/20 text-white hover:bg-white/5"
                                          }
                                        >
                                          <Heart className={`w-3 h-3 mr-1 ${shortlistedIds.includes(shortlistKey(applicant.id)) ? 'fill-current' : ''}`} />
                                          {shortlistedIds.includes(shortlistKey(applicant.id)) ? 'Shortlisted' : 'Shortlist'}
                                        </Button>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="border-white/20 text-white hover:bg-white/5"
                                          onClick={() => {
                                            setSelectedCandidate(applicant);
                                            openConversationForCandidate(applicant.id);
                                          }}
                                        >
                                          <MessageSquare className="w-3 h-3 mr-1" />
                                          Message
                                        </Button>
                                        <Link to={`/employer/candidate/${applicant.id}`}>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="border-white/20 text-white hover:bg-white/5"
                                          >
                                            <FileText className="w-3 h-3 mr-1" />
                                            View Profile
                                          </Button>
                                        </Link>
                                      </div>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Candidates Tab */}
          <TabsContent value="candidates" className="space-y-6">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Search Candidates</CardTitle>
                <CardDescription className="text-gray-400">
                  Find the perfect match using AI-powered search
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      placeholder="Job title, skills, or keywords"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                    />
                  </div>
                  <Select defaultValue="stevenage">
                    <SelectTrigger className="w-full md:w-48 bg-white/10 border-white/20 text-white">
                      <SelectValue placeholder="Location" />
                    </SelectTrigger>
                    <SelectContent className="role-surface-employer border-white/10 bg-role-employer-overlay text-white backdrop-blur-xl">
                      <SelectItem value="stevenage">Stevenage</SelectItem>
                      <SelectItem value="hitchin">Hitchin</SelectItem>
                      <SelectItem value="letchworth">Letchworth</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button className="bg-gold-gradient hover:brightness-110 text-black">
                    <Filter className="w-4 h-4 mr-2" />
                    Filters
                  </Button>
                </div>

                <div className="space-y-4">
                    {candidateMatches.map((candidate) => (
                    <Card
                      key={candidate.id}
                      className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)] transition-all duration-300"
                    >
                      <CardContent className="p-6">
                        <div className="flex flex-col lg:flex-row gap-6">
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex items-start gap-4">
                                <Avatar className="w-16 h-16">
                                  <AvatarFallback className="bg-gradient-to-br from-[#ae8625] to-[#f7ef8a] text-black text-xl">
                                    {candidate.name
                                      .split(' ')
                                      .map((n: string) => n[0] ?? '')
                                      .join('')}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <h3 className="text-white">{candidate.name}</h3>
                                    <Badge className="bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50">
                                      <Award className="w-3 h-3 mr-1" />
                                      {candidate.aiScore}% Match
                                    </Badge>
                                  </div>
                                  <p className="text-gray-300 mb-2">{candidate.title}</p>
                                  <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                                    <span className="flex items-center gap-1">
                                      <MapPin className="w-4 h-4" />
                                      {candidate.location}
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <Clock className="w-4 h-4" />
                                      {candidate.experience}
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <DollarSign className="w-4 h-4" />
                                      {candidate.salary}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="mb-4">
                              <p className="text-sm text-gray-300 mb-3">{candidate.summary}</p>
                              <div className="flex flex-wrap gap-2">
                                {candidate.skills.map((skill: string, idx: number) => (
                                  <Badge
                                    key={idx}
                                    variant="outline"
                                    className="border-white/20 text-gray-300"
                                  >
                                    {skill}
                                  </Badge>
                                ))}
                              </div>
                            </div>

                            <div className="bg-white/5 rounded-lg p-4 mb-4">
                              <div className="flex items-center gap-2 mb-2">
                                <Sparkles className="w-4 h-4 text-[#ba9036]" />
                                <span className="text-sm text-[#ba9036]">AI Match Analysis</span>
                              </div>
                              <Progress value={candidate.aiScore} className="mb-2 h-2" />
                              <ul className="text-sm text-gray-300 space-y-1">
                                {candidate.matchReasons.map((reason, idx) => (
                                  <li key={idx} className="flex items-start gap-2">
                                    <span className="text-[#ba9036]">•</span>
                                    {reason}
                                  </li>
                                ))}
                              </ul>
                            </div>

                            <div className="flex flex-wrap gap-3">
                              <Button
                                onClick={() => toggleShortlist(candidate.id)}
                                variant={shortlistedIds.includes(shortlistKey(candidate.id)) ? "default" : "outline"}
                                className={
                                  shortlistedIds.includes(shortlistKey(candidate.id))
                                    ? "bg-gold-gradient text-black"
                                    : "border-white/20 text-white hover:bg-white/5"
                                }
                              >
                                <Heart className={`w-4 h-4 mr-2 ${shortlistedIds.includes(shortlistKey(candidate.id)) ? 'fill-current' : ''}`} />
                                {shortlistedIds.includes(shortlistKey(candidate.id)) ? 'Shortlisted' : 'Shortlist'}
                              </Button>
                              <Button
                                variant="outline"
                                className="border-white/20 text-white hover:bg-white/5"
                                onClick={() => {
                                  setSelectedCandidate(candidate);
                                  openConversationForCandidate(candidate.id);
                                }}
                              >
                                <MessageSquare className="w-4 h-4 mr-2" />
                                Message
                              </Button>
                              <Link to={`/employer/candidate/${candidate.id}`}>
                                <Button
                                  variant="outline"
                                  className="border-white/20 text-white hover:bg-white/5"
                                >
                                  <FileText className="w-4 h-4 mr-2" />
                                  View Full Profile
                                </Button>
                              </Link>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs" className="space-y-6">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white">My Job Postings</CardTitle>
                    <CardDescription className="text-gray-400">
                      Manage your active and past job listings
                    </CardDescription>
                  </div>
                  <Button
                    asChild
                    className="bg-gold-gradient hover:brightness-110 text-black"
                  >
                    <Link to="/employer/job/new">
                      <Plus className="w-4 h-4 mr-2" />
                      Post Job
                    </Link>
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {jobPosts.map((job) => (
                  <Card
                    key={job.id}
                    className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)] transition-all duration-300"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-white mb-2">{job.title}</h3>
                          <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {job.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <DollarSign className="w-4 h-4" />
                              {job.salary}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {job.type}
                            </span>
                          </div>
                        </div>
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                          {job.status}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4 mb-4 p-4 bg-white/5 rounded-lg">
                        <div>
                          <div className="text-white mb-1">{job.applicants}</div>
                          <p className="text-xs text-gray-400">Applicants</p>
                        </div>
                        <div>
                          <div className="text-white mb-1">{job.views}</div>
                          <p className="text-xs text-gray-400">Views</p>
                        </div>
                        <div>
                          <div className="text-white mb-1">{job.posted}</div>
                          <p className="text-xs text-gray-400">Posted</p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              className="border-white/20 text-white hover:bg-white/5"
                              onClick={() => setViewingApplicantsForJob(job)}
                            >
                              <Users className="w-3 h-3 mr-1" />
                              View Applicants ({job.applicants})
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="role-surface-employer border-white/10 bg-role-employer-overlay text-white backdrop-blur-xl max-w-6xl max-h-[90vh] overflow-hidden">
                            <DialogHeader>
                              <DialogTitle className="text-gold-gradient">
                                Applicants for {job.title}
                              </DialogTitle>
                              <DialogDescription className="text-gray-400">
                                Review, filter, and manage candidate applications
                              </DialogDescription>
                            </DialogHeader>
                            {viewingApplicantsForJob && selectedJobId === String(job.id) && (
                              <ViewApplicants
                                job={viewingApplicantsForJob}
                                applicants={applicantsByJobId.get(selectedJobId ?? '') ?? []}
                                loading={employerApplicantsQuery.isPending}
                                onClose={() => setViewingApplicantsForJob(null)}
                              />
                            )}
                          </DialogContent>
                        </Dialog>
                        <Button
                          variant="outline"
                          className="border-white/20 text-white hover:bg-white/5"
                          onClick={() => navigate(`/employer/job/${job.id}/edit`, { state: { jobId: job.id } })}
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          className="text-red-300 hover:text-red-200"
                          onClick={() => handleDeleteJob(job.id)}
                        >
                          <Trash2 className="w-3 h-3 mr-1" aria-hidden />
                          Delete
                        </Button>
                        <Button
                          variant="outline"
                          className="border-[#d2ac47]/50 text-[#ba9036] hover:bg-[#ba9036]/10"
                          onClick={() => toast.info('Boost feature coming soon!')}
                        >
                          <TrendingUp className="w-3 h-3 mr-1" />
                          Boost Post
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Shortlist Tab */}
          <TabsContent value="shortlist" className="space-y-6">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Star className="w-5 h-5 text-[#ba9036]" />
                  Shortlisted Candidates
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Your top candidate selections ({shortlistedIds.length} candidates)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {shortlistedIds.length === 0 ? (
                  <div className="text-center py-12">
                    <Star className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-white mb-2">No shortlisted candidates yet</h3>
                    <p className="text-gray-400 mb-6">Start shortlisting candidates from the Find Candidates tab</p>
                    <Button
                      onClick={() => setActiveTab('candidates')}
                      className="bg-gold-gradient hover:brightness-110 text-black"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Find Candidates
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {candidateMatches
                      .filter((candidate) => shortlistedIds.includes(shortlistKey(candidate.id)))
                      .map((candidate) => (
                        <Card
                          key={candidate.id}
                          className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 hover:shadow-[0_0_30px_rgba(210,172,71,0.3)] transition-all duration-300"
                        >
                          <CardContent className="p-6">
                            <div className="flex flex-col lg:flex-row gap-6">
                              <div className="flex-1">
                                <div className="flex items-start justify-between mb-4">
                                  <div className="flex items-start gap-4">
                                    <Avatar className="w-16 h-16">
                                      <AvatarFallback className="bg-gradient-to-br from-[#ae8625] to-[#f7ef8a] text-black text-xl">
                                        {candidate.name
                                          .split(' ')
                                          .map((n: string) => n[0] ?? '')
                                          .join('')}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <div className="flex items-center gap-2 mb-1">
                                        <h3 className="text-white">{candidate.name}</h3>
                                        <Badge className="bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50">
                                          <Award className="w-3 h-3 mr-1" />
                                          {candidate.aiScore}% Match
                                        </Badge>
                                      </div>
                                      <p className="text-gray-300 mb-2">{candidate.title}</p>
                                      <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                                        <span className="flex items-center gap-1">
                                          <MapPin className="w-4 h-4" />
                                          {candidate.location}
                                        </span>
                                        <span className="flex items-center gap-1">
                                          <Clock className="w-4 h-4" />
                                          {candidate.experience}
                                        </span>
                                        <span className="flex items-center gap-1">
                                          <DollarSign className="w-4 h-4" />
                                          {candidate.salary}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div className="mb-4">
                                  <p className="text-sm text-gray-300 mb-3">{candidate.summary}</p>
                                  <div className="flex flex-wrap gap-2">
                                    {candidate.skills.map((skill: string, idx: number) => (
                                      <Badge
                                        key={idx}
                                        variant="outline"
                                        className="border-white/20 text-gray-300"
                                      >
                                        {skill}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>

                                <div className="flex flex-wrap gap-3">
                                  <Button
                                    onClick={() => toggleShortlist(candidate.id)}
                                    className="bg-[#ba9036]/20 text-[#ba9036] border border-[#d2ac47]/50 hover:bg-[#ba9036]/30"
                                  >
                                    <Heart className="w-4 h-4 mr-2 fill-current" />
                                    Remove from Shortlist
                                  </Button>
                                  <Button
                                    variant="outline"
                                    className="border-white/20 text-white hover:bg-white/5"
                                    onClick={() => {
                                      setSelectedCandidate(candidate);
                                      openConversationForCandidate(candidate.id);
                                    }}
                                  >
                                    <MessageSquare className="w-4 h-4 mr-2" />
                                    Message
                                  </Button>
                                  <Link to={`/employer/candidate/${candidate.id}`}>
                                    <Button
                                      variant="outline"
                                      className="border-white/20 text-white hover:bg-white/5"
                                    >
                                      <FileText className="w-4 h-4 mr-2" />
                                      View Full Profile
                                    </Button>
                                  </Link>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Talent Pools Tab */}
          <TabsContent value="talent-pools" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Talent Pools Section */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Database className="w-5 h-5 text-[#ba9036]" />
                        Talent Pools
                      </CardTitle>
                      <CardDescription className="text-gray-400">
                        Curated groups of candidates for quick access
                      </CardDescription>
                    </div>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button className="bg-gold-gradient hover:brightness-110 text-black">
                          <Plus className="w-4 h-4 mr-2" />
                          New Pool
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="role-surface-employer border-white/10 bg-role-employer-overlay text-white backdrop-blur-xl">
                        <DialogHeader>
                          <DialogTitle className="text-gold-gradient">
                            Create Talent Pool
                          </DialogTitle>
                          <DialogDescription className="text-gray-400">
                            Define criteria to automatically group candidates
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label className="text-white">Pool Name</Label>
                            <Input
                              placeholder="e.g. Senior Care Professionals"
                              className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-white">Criteria</Label>
                            <Textarea
                              placeholder="Define skills, experience, location, etc."
                              className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                            />
                          </div>
                          <Button className="w-full bg-gold-gradient hover:brightness-110 text-black">
                            Create Pool
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {talentSearchPreview.isPending && (
                    <p className="text-sm text-gray-400">Scanning talent pools…</p>
                  )}
                  {talentSearchPreview.isError && !talentSearchPreview.isPending && (
                    <p className="text-sm text-red-300">Unable to load talent pools right now.</p>
                  )}
                  {!talentSearchPreview.isPending && !talentSearchPreview.isError && talentPools.length === 0 && (
                    <p className="text-sm text-gray-400">No candidates match your pool criteria yet.</p>
                  )}
                  {talentPools.map((pool) => (
                    <Card
                      key={pool.id}
                      className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 hover:shadow-[0_0_20px_rgba(210,172,71,0.2)] transition-all duration-300"
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="text-white mb-1">{pool.name}</h4>
                            <p className="text-xs text-gray-400">{pool.criteria}</p>
                          </div>
                          <Badge className="bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50">
                            {pool.matchRate}%
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-400">
                            <Users className="w-4 h-4 inline mr-1" />
                            {pool.candidateCount} candidates
                          </span>
                          <span className="text-gray-500 text-xs">Updated {pool.lastUpdated}</span>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 border-white/20 text-white hover:bg-white/5"
                            onClick={() => navigate('/employer/search', { state: { initialFilters: { returnerFriendly: true } } })}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-white/20 text-white hover:bg-white/5"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </CardContent>
              </Card>

              {/* Saved Searches Section */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Bookmark className="w-5 h-5 text-[#ba9036]" />
                        Saved Searches
                        {notifications && (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/50 ml-2">
                            <Bell className="w-3 h-3 mr-1" />
                            Live
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription className="text-gray-400">
                        Get notified when new candidates match your criteria
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          const previous = notifications;
                          const next = !notifications;
                          setNotifications(next);
                          toast.success(
                            next
                              ? 'Notifications enabled! You\'ll be notified of new matches.'
                              : 'Notifications disabled',
                            {
                              duration: 4000,
                              action: {
                                label: 'Undo',
                                onClick: () => setNotifications(previous),
                              },
                            },
                          );
                        }}
                        className={`border-white/20 ${
                          notifications
                            ? 'bg-[#ba9036]/10 text-[#ba9036] border-[#d2ac47]/50'
                            : 'text-white hover:bg-white/5'
                        }`}
                      >
                        <Bell className="w-4 h-4" />
                      </Button>
                      <Button className="bg-gold-gradient hover:brightness-110 text-black" onClick={handleEmployerSaveSearch} disabled={savedSearchesLoading}>
                        <Plus className="w-4 h-4 mr-2" />
                        Save Search
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {savedSearchesLoading && <p className="text-sm text-gray-400">Loading saved searches...</p>}
                  {savedSearchesError && !savedSearchesLoading && (
                    <p className="text-sm text-red-300">Could not load saved searches. Please try again.</p>
                  )}
                  {!savedSearchesLoading && !savedSearchesError && savedSearches.length === 0 && (
                    <p className="text-sm text-gray-400">No saved searches yet. Save a search to start receiving alerts.</p>
                  )}
                  {!savedSearchesLoading && !savedSearchesError && savedSearches.length > 0 && (
                    savedSearches.map((search) => (
                      <Card
                        key={search.id}
                        className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 hover:shadow-[0_0_20px_rgba(210,172,71,0.2)] transition-all duration-300"
                      >
                        <CardContent className="space-y-4 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <h4 className="text-white">{search.name}</h4>
                              <p className="text-xs text-gray-400">{describeSavedSearch(search)}</p>
                              {search.tags?.length ? (
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {search.tags.map((tag) => (
                                    <Badge key={`${search.id}-${tag}`} variant="outline" className="border-white/20 text-[0.65rem] uppercase tracking-wide text-gray-200">
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                              ) : null}
                              {summarisePayPreference(search) && (
                                <p className="mt-1 text-[0.65rem] uppercase tracking-wide text-gray-500">
                                  {summarisePayPreference(search)}
                                </p>
                              )}
                            </div>
                            {search.cadence !== 'off' && (
                              <Badge className="bg-green-500/20 text-green-300 border-green-500/40">
                                {search.cadence === 'daily' ? 'Daily alerts' : 'Weekly alerts'}
                              </Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-gray-500">
                            <span>Created {createdLabelForSearch(search)}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] uppercase tracking-[0.3em] text-gray-500">Cadence</span>
                              <Select
                                value={search.cadence}
                                onValueChange={(value) => updateSavedSearchCadence(search.id, value as NotificationCadence)}
                              >
                                <SelectTrigger className="h-8 w-28 border-white/20 bg-white/10 text-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="border-white/10 bg-[#0d1022] text-white">
                                  <SelectItem value="daily">Daily</SelectItem>
                                  <SelectItem value="weekly">Weekly</SelectItem>
                                  <SelectItem value="off">Off</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 text-[#ba9036] hover:text-[#f7ef8a]"
                              onClick={() => handleViewSavedSearch(search)}
                            >
                              <Bookmark className="w-3 h-3 mr-2" />
                              View matches
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 text-white hover:text-gray-300"
                              onClick={() => removeSavedSearch(search.id)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Quick Access to Pools */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white">Pool Management Tips</CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      toast.info('New candidate matches your search: "Care Assistants - Stevenage"', {
                        action: {
                          label: 'View',
                          onClick: () => toast.success('Navigating to matches...'),
                        },
                        duration: 10000,
                      });
                    }}
                    className="border-[#d2ac47]/50 text-[#ba9036] hover:bg-[#ba9036]/10"
                  >
                    <Bell className="w-4 h-4 mr-2" />
                    Test Notification
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                    <Sparkles className="w-8 h-8 text-[#ba9036] mb-2" />
                    <h4 className="text-white mb-1 text-sm">AI-Powered Matching</h4>
                    <p className="text-xs text-gray-400">
                      Our AI automatically updates pools with the best matching candidates
                    </p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                    <Bell className="w-8 h-8 text-[#ba9036] mb-2" />
                    <h4 className="text-white mb-1 text-sm">Real-time Alerts</h4>
                    <p className="text-xs text-gray-400">
                      Get notified when new candidates join your talent pools {notifications && '(Active)'}
                    </p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                    <Users className="w-8 h-8 text-[#ba9036] mb-2" />
                    <h4 className="text-white mb-1 text-sm">Bulk Actions</h4>
                    <p className="text-xs text-gray-400">
                      Message or shortlist multiple candidates from a pool at once
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-[#ba9036]" />
                      Job Performance Analytics
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      Track and optimize your job posting performance
                    </CardDescription>
                  </div>
                  <Button
                    onClick={exportAnalytics}
                    className="bg-gold-gradient hover:brightness-110 text-black"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export Data
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4 grid gap-3 md:grid-cols-[2fr_1fr]">
                  <div className="space-y-2">
                    <label className="text-sm text-gray-300" htmlFor="analytics-occupation">
                      Filter by occupation (DB-backed)
                    </label>
                    <Input
                      id="analytics-occupation"
                      value={analyticsOccupationQuery}
                      onChange={(event) => setAnalyticsOccupationQuery(event.target.value)}
                      placeholder="e.g. Healthcare Assistant"
                      className="border-white/10 bg-white/10 text-white placeholder:text-gray-400"
                    />
                    {analyticsRoleSuggestions.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {analyticsRoleSuggestions.map((role) => (
                          <Badge
                            key={role.id}
                            className="cursor-pointer border-white/15 bg-white/5 text-xs text-gray-100 hover:bg-white/10"
                            onClick={() => {
                              setAnalyticsOccupationQuery(role.preferredLabel);
                              setAnalyticsOccupationId(role.id);
                            }}
                          >
                            {role.preferredLabel}
                          </Badge>
                        ))}
                        {analyticsOccupationId && (
                          <Button
                            type="button"
                            variant="ghost"
                            className="text-xs text-gray-400 hover:text-white"
                            onClick={() => {
                              setAnalyticsOccupationQuery('');
                              setAnalyticsOccupationId(undefined);
                            }}
                          >
                            Clear filter
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  {[
                    { label: 'Total Views', value: analyticsSummary.totalViews.toLocaleString(), icon: Eye },
                    { label: 'Applications', value: analyticsSummary.totalApplications.toLocaleString(), icon: UserPlus },
                    { label: 'Avg. Conversion', value: `${analyticsSummary.avgConversion.toFixed(1)}%`, icon: TrendingUp },
                    { label: 'Engagement', value: `${analyticsSummary.engagement}%`, icon: ThumbsUp },
                  ].map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                      <div
                        key={index}
                        className="p-4 bg-white/5 rounded-lg border border-white/10"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <Icon className="w-5 h-5 text-[#ba9036]" />
                        </div>
                        <div className="text-white text-2xl mb-1">{stat.value}</div>
                        <p className="text-xs text-gray-400">{stat.label}</p>
                      </div>
                    );
                  })}
                </div>

                <div className="space-y-4">
                  {analyticsDisplayRows.map((job, index) => (
                    <Card
                      key={index}
                      className="bg-white/5 border-white/10 hover:border-[#d2ac47]/50 hover:shadow-[0_0_20px_rgba(210,172,71,0.2)] transition-all duration-300"
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <h3 className="text-white">{job.jobTitle}</h3>
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                            Active
                          </Badge>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                          <div>
                            <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
                              <Eye className="w-3 h-3" />
                              Views
                            </div>
                            <div className="text-white">{job.views}</div>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
                              <MousePointer className="w-3 h-3" />
                              Applications
                            </div>
                            <div className="text-white">{job.applications}</div>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
                              <TrendingUp className="w-3 h-3" />
                              Conversion
                            </div>
                            <div className="text-white">{job.conversionRate}%</div>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
                              <Clock className="w-3 h-3" />
                              Avg. Time
                            </div>
                            <div className="text-white">{job.avgTimeToApply}</div>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
                              <Award className="w-3 h-3" />
                              Top Source
                            </div>
                            <div className="text-white text-sm">{job.topSource}</div>
                          </div>
                        </div>

                        <div className="mt-4 pt-4 border-t border-white/10">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-400">Performance Score</span>
                            <span className="text-[#ba9036]">{job.conversionRate > 7 ? 'Excellent' : 'Good'}</span>
                          </div>
                          <Progress value={job.conversionRate * 10} className="mt-2" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Optimization Tips</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex gap-3 p-3 bg-[#ba9036]/10 rounded-lg border border-[#d2ac47]/20">
                    <Sparkles className="w-5 h-5 text-[#ba9036] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-white text-sm mb-1">Improve job title clarity</p>
                      <p className="text-xs text-gray-400">
                        Jobs with specific titles get 23% more applications
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3 p-3 bg-white/5 rounded-lg border border-white/10">
                    <TrendingUp className="w-5 h-5 text-[#ba9036] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-white text-sm mb-1">Post during peak hours</p>
                      <p className="text-xs text-gray-400">
                        Tuesday-Thursday 9-11 AM sees highest engagement
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3 p-3 bg-white/5 rounded-lg border border-white/10">
                    <Award className="w-5 h-5 text-[#ba9036] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-white text-sm mb-1">Add salary transparency</p>
                      <p className="text-xs text-gray-400">
                        Jobs with visible salary get 40% more applications
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Performance Benchmarks</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-gray-400">Your Avg. Conversion Rate</span>
                      <span className="text-white">7.2%</span>
                    </div>
                    <Progress value={72} className="mb-1" />
                    <p className="text-xs text-green-400">Above industry average (5.8%)</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-gray-400">Response Time</span>
                      <span className="text-white">1.2 days</span>
                    </div>
                    <Progress value={88} className="mb-1" />
                    <p className="text-xs text-green-400">Excellent (Industry: 2.5 days)</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-gray-400">Candidate Satisfaction</span>
                      <span className="text-white">4.7/5</span>
                    </div>
                    <Progress value={94} className="mb-1" />
                    <p className="text-xs text-green-400">Outstanding rating</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Branding Tab */}
          <TabsContent value="branding" className="space-y-6">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Palette className="w-5 h-5 text-[#ba9036]" />
                  Employer Branding Suite
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Showcase your company culture and attract top talent
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Company Profile */}
                  <div className="space-y-4">
                    <h3 className="text-white flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-[#ba9036]" />
                      Company Profile
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-white text-sm">Company Name</Label>
                        <Input
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-white text-sm">Tagline</Label>
                        <Input
                          value={companyTagline}
                          onChange={(e) => setCompanyTagline(e.target.value)}
                          placeholder="e.g. Caring for our community since 2010"
                          className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-white text-sm">Company Description</Label>
                        <Textarea
                          value={companyDescription}
                          onChange={(e) => setCompanyDescription(e.target.value)}
                          placeholder="Tell candidates about your company, culture, and values..."
                          className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 mt-1 min-h-32"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Visual Branding */}
                  <div className="space-y-4">
                    <h3 className="text-white flex items-center gap-2">
                      <Image className="w-4 h-4 text-[#ba9036]" />
                      Visual Identity
                    </h3>
                    <div className="space-y-4">
                      <div className="p-6 bg-white/5 rounded-lg border border-white/10 border-dashed text-center">
                        {companyLogo?.publicUrl || companyLogo?.fileUrl ? (
                          <img
                            src={companyLogo.publicUrl ?? companyLogo.fileUrl}
                            alt="Company logo"
                            className="w-20 h-20 mx-auto mb-3 object-contain"
                          />
                        ) : (
                          <Image className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                        )}
                        <p className="text-sm text-gray-400 mb-2">Company Logo</p>
                        <label className="inline-block">
                          <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/5 cursor-pointer">
                            <span>{logoUploading ? 'Uploading…' : 'Upload Logo'}</span>
                          </Button>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleLogoUpload}
                          />
                        </label>
                      </div>
                      <div className="p-6 bg-white/5 rounded-lg border border-white/10 border-dashed text-center">
                        {companyCover?.publicUrl || companyCover?.fileUrl ? (
                          <img
                            src={companyCover.publicUrl ?? companyCover.fileUrl}
                            alt="Cover"
                            className="w-full h-24 object-cover rounded-lg mb-3"
                          />
                        ) : (
                          <Image className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                        )}
                        <p className="text-sm text-gray-400 mb-2">Cover Image</p>
                        <label className="inline-block">
                          <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/5 cursor-pointer">
                            <span>{coverUploading ? 'Uploading…' : 'Upload Image'}</span>
                          </Button>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleCoverUpload}
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Culture & Benefits */}
                <div className="space-y-4">
                  <h3 className="text-white flex items-center gap-2">
                    <Star className="w-4 h-4 text-[#ba9036]" />
                    Company Culture & Benefits
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    {benefits.map((benefit) => {
                      const Icon = benefit.icon;
                      return (
                        <button
                          type="button"
                          key={benefit.id}
                          onClick={() => toggleBenefit(benefit.id)}
                          className={`p-4 text-left rounded-lg border transition-all group ${
                            benefit.selected
                              ? 'border-[#d2ac47]/70 bg-[#d2ac47]/10'
                              : 'bg-white/5 border-white/10 hover:border-[#d2ac47]/50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#f7ef8a]/20 to-[#d2ac47]/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                              <Icon className="w-5 h-5 text-[#ba9036]" />
                            </div>
                            <span className="text-white text-sm">{benefit.title}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <Button type="button" onClick={addCustomBenefit} variant="outline" className="border-white/20 text-white hover:bg-white/5">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Custom Benefit
                  </Button>
                </div>

                {/* Company Photos/Videos */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-white flex items-center gap-2">
                      <Image className="w-4 h-4 text-[#ba9036]" />
                      Workplace Gallery
                    </h3>
                    <label htmlFor="photo-upload">
                      <Button
                        type="button"
                        variant="outline"
                        className="border-white/20 text-white hover:bg-white/5 cursor-pointer"
                        onClick={() => document.getElementById('photo-upload')?.click()}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Photos
                      </Button>
                      <input
                        id="photo-upload"
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handlePhotoUpload}
                      />
                    </label>
                  </div>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-gray-400">Drag and drop photos to reorder them</p>
                      {companyPhotos.length > 0 && (
                        <Badge variant="outline" className="border-green-500/50 text-green-400 text-xs">
                          {companyPhotos.length} uploaded {galleryUploading ? '(uploading...)' : ''}
                        </Badge>
                      )}
                    </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {companyPhotos.map((photo, index) => (
                      <div
                        key={index}
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDrop={(e) => handleDrop(e, index)}
                        className="aspect-square bg-white/5 rounded-lg border border-white/10 relative overflow-hidden group cursor-move hover:border-[#d2ac47]/50 transition-all"
                      >
                        <img
                          src={photo.publicUrl ?? photo.fileUrl}
                          alt={`Company photo ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Button
                            type="button"
                            size="icon"
                            variant="destructive"
                            onClick={() => void removePhoto(index)}
                            className="bg-red-500 hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {companyPhotos.length < 8 && (
                      <label
                        htmlFor="photo-upload-extra"
                        className="aspect-square bg-white/5 rounded-lg border border-white/10 border-dashed flex items-center justify-center hover:border-[#d2ac47]/50 transition-all cursor-pointer"
                      >
                        <div className="text-center">
                          <Plus className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                          <p className="text-xs text-gray-400">Add Photo</p>
                        </div>
                        <input
                          id="photo-upload-extra"
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={handlePhotoUpload}
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Save Changes */}
                <div className="flex gap-3 pt-4 border-t border-white/10">
                  <Button type="button" onClick={handleSavePublish} className="bg-gold-gradient hover:brightness-110 text-black">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Save & Publish
                  </Button>
                  <Button type="button" onClick={handlePreviewProfile} variant="outline" className="border-white/20 text-white hover:bg-white/5">
                    <Eye className="w-4 h-4 mr-2" />
                    Preview Profile
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Branding Impact Stats */}
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#f7ef8a]/20 to-[#d2ac47]/20 flex items-center justify-center">
                      <Eye className="w-6 h-6 text-[#ba9036]" />
                    </div>
                    <div>
                      <div className="text-white text-2xl">+47%</div>
                      <p className="text-xs text-gray-400">Profile Views</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400">
                    Employers with complete profiles get 47% more views
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#f7ef8a]/20 to-[#d2ac47]/20 flex items-center justify-center">
                      <Users className="w-6 h-6 text-[#ba9036]" />
                    </div>
                    <div>
                      <div className="text-white text-2xl">+62%</div>
                      <p className="text-xs text-gray-400">Applications</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400">
                    Strong branding increases quality applications
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#f7ef8a]/20 to-[#d2ac47]/20 flex items-center justify-center">
                      <ThumbsUp className="w-6 h-6 text-[#ba9036]" />
                    </div>
                    <div>
                      <div className="text-white text-2xl">4.8/5</div>
                      <p className="text-xs text-gray-400">Candidate Rating</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400">
                    Your employer brand reputation score
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Skill Tests Tab */}
          <TabsContent value="skill-tests" className="space-y-6">
            <SkillTestManager />
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="space-y-6">
            <div className="grid gap-6 lg:grid-cols-[320px,1fr]">
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-[#ba9036]" />
                    Inbox
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    View conversations with candidates across your roles
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {conversationsQuery.isPending ? (
                    Array.from({ length: 3 }).map((_, idx) => (
                      <div key={`conv-skeleton-${idx}`} className="animate-pulse rounded-xl border border-white/10 bg-white/5 p-4">
                        <div className="mb-2 h-5 w-32 rounded bg-white/10" />
                        <div className="h-3 w-48 rounded bg-white/10" />
                      </div>
                    ))
                  ) : conversationList.length === 0 ? (
                    <p className="text-sm text-gray-400">No conversations yet. Start chatting from Talent Search.</p>
                  ) : (
                    conversationList.map((conversation) => (
                      <button
                        key={conversation.id}
                        type="button"
                        onClick={() => setSelectedConversationId(conversation.id)}
                        className={`w-full text-left rounded-xl border p-3 transition ${
                          selectedConversationId === conversation.id
                            ? 'border-[#d2ac47]/60 bg-white/10'
                            : 'border-white/10 bg-transparent hover:border-[#d2ac47]/40'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-white text-sm">{conversation.candidateName}</p>
                            <p className="text-xs text-gray-400">{conversation.jobTitle}</p>
                          </div>
                          <div className="text-right text-xs text-gray-500">
                            {formatRelativeTime(conversation.lastMessageTime)}
                            {conversation.unread > 0 && (
                              <Badge className="ml-2 bg-[#ba9036]/20 text-[#ba9036]">{conversation.unread}</Badge>
                            )}
                          </div>
                        </div>
                        <p className="mt-2 text-xs text-gray-400 line-clamp-2">{conversation.lastMessage}</p>
                      </button>
                    ))
                  )}
                </CardContent>
              </Card>

              <Card className="bg-white/5 border-white/10">
                {selectedConversation ? (
                  <>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-3 text-white">
                        <Avatar>
                          <AvatarFallback className="bg-gradient-to-br from-[#ae8625] to-[#f7ef8a] text-black">
                            {selectedConversation.candidateName
                              .split(' ')
                              .map((n) => n[0])
                              .join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p>{selectedConversation.candidateName}</p>
                          <p className="text-sm text-gray-400">
                            {selectedConversation.candidateTitle} · {selectedConversation.jobTitle}
                          </p>
                        </div>
                      </CardTitle>
                      <CardDescription className="text-gray-400">
                        End-to-end encrypted · {conversationMessages.length} messages
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <ScrollArea className="h-96 rounded-xl border border-white/10 bg-white/[0.02] p-4">
                        {conversationMessagesQuery.isPending ? (
                          <div className="text-center text-sm text-gray-400">Loading messages…</div>
                        ) : conversationMessages.length === 0 ? (
                          <div className="text-center text-sm text-gray-400">No messages yet.</div>
                        ) : (
                          <div className="space-y-4">
                            {conversationMessages.map((message: ConversationMessage) => (
                              <div
                                key={message.id}
                                className={`flex ${message.sender === 'employer' ? 'justify-end' : 'justify-start'}`}
                              >
                                <div
                                  className={`rounded-lg p-3 max-w-[80%] ${
                                    message.sender === 'employer'
                                      ? 'bg-gold-gradient text-black'
                                      : 'bg-white/10 text-gray-200'
                                  }`}
                                >
                                  <p className="text-sm whitespace-pre-line">{message.body}</p>
                                  {message.attachments?.length ? (
                                    <div className="mt-2 space-y-1 text-xs">
                                      {message.attachments.map((attachment: UploadedFileMeta) => (
                                        <a
                                          key={attachment.fileUrl}
                                          href={attachment.fileUrl}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="flex items-center gap-2 text-[#1f2933]"
                                        >
                                          <Paperclip className="h-3 w-3" />
                                          <span className={message.sender === 'employer' ? 'text-black' : 'text-gray-200'}>
                                            {attachment.fileName}
                                          </span>
                                        </a>
                                      ))}
                                    </div>
                                  ) : null}
                                  <span
                                    className={`mt-1 block text-[0.65rem] ${
                                      message.sender === 'employer' ? 'text-black/60' : 'text-gray-400'
                                    }`}
                                  >
                                    {formatRelativeTime(message.createdAt)}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </ScrollArea>
                      {isPartnerTyping && (
                        <p className="text-xs text-gray-400">Candidate is typing…</p>
                      )}
                      {composerAttachments.length > 0 && (
                        <div className="flex flex-wrap gap-2 text-xs text-gray-200">
                          {composerAttachments.map((attachment) => (
                            <Badge
                              key={attachment.uploadId ?? attachment.fileUrl}
                              className="bg-white/10 text-gray-100 cursor-pointer"
                              onClick={() => {
                                void removeAttachment(attachment.uploadId ?? attachment.fileUrl);
                              }}
                            >
                              {attachment.fileName} ×
                            </Badge>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <input
                          type="file"
                          ref={fileInputRef}
                          className="hidden"
                          onChange={handleAttachmentUpload}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className="border-white/20 text-white hover:bg-white/5"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={attachmentUploading}
                        >
                          {attachmentUploading ? 'Uploading…' : 'Attach'}
                        </Button>
                        <Input
                          placeholder="Type your message..."
                          value={messageText}
                          onChange={(e) => setMessageText(e.target.value)}
                          onKeyUp={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendMessage();
                            }
                          }}
                          className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                        />
                        <Button
                          onClick={handleSendMessage}
                          className="bg-gold-gradient hover:brightness-110 text-black"
                          disabled={sendConversationMessageMutation.isPending}
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Shield className="w-3 h-3" />
                        End-to-end encrypted
                      </p>
                    </CardContent>
                  </>
                ) : (
                  <CardContent className="text-center py-12 text-gray-400">
                    <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Select a conversation to start messaging</p>
                  </CardContent>
                )}
              </Card>
            </div>
          </TabsContent>
          </div>
          </div>
        </Tabs>
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-4xl border-white/10 bg-role-employer-overlay text-white">
            <DialogHeader>
              <DialogTitle className="text-gold-gradient">Employer Profile Preview</DialogTitle>
              <DialogDescription className="text-gray-400">
                This is how candidates will see your company profile.
              </DialogDescription>
            </DialogHeader>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-400 mb-1">Company</p>
                  <p className="text-lg text-white font-semibold">{companyName || 'Untitled Company'}</p>
                  {companyTagline && <p className="text-sm text-gray-300">{companyTagline}</p>}
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-gray-400">Description</p>
                  <p className="text-sm text-gray-200 whitespace-pre-wrap">
                    {companyDescription || 'Add a company description to share your mission and values.'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-2">Benefits</p>
                  <div className="flex flex-wrap gap-2">
                    {benefits.filter((b) => b.selected).map((b) => (
                      <Badge key={b.id} className="bg-[#d2ac47]/15 text-[#d2ac47] border-[#d2ac47]/40">
                        {b.title}
                      </Badge>
                    ))}
                    {benefits.every((b) => !b.selected) && (
                      <p className="text-xs text-gray-500">No benefits selected yet.</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="rounded-lg border border-white/10 bg-white/5 overflow-hidden">
                  {companyCover?.publicUrl || companyCover?.fileUrl ? (
                    <img src={companyCover.publicUrl ?? companyCover.fileUrl} alt="Cover" className="w-full h-40 object-cover" />
                  ) : (
                    <div className="h-40 flex items-center justify-center text-gray-500 text-sm bg-white/5">
                      Cover image placeholder
                    </div>
                  )}
                  <div className="p-4 flex items-center gap-3">
                    {companyLogo?.publicUrl || companyLogo?.fileUrl ? (
                      <img src={companyLogo.publicUrl ?? companyLogo.fileUrl} alt="Logo" className="w-12 h-12 rounded bg-white/10 object-contain p-2" />
                    ) : (
                      <div className="w-12 h-12 rounded bg-white/10 flex items-center justify-center text-gray-500">
                        <Image className="w-5 h-5" />
                      </div>
                    )}
                    <div>
                      <p className="text-white text-sm">{companyName || 'Untitled Company'}</p>
                      {companyTagline && <p className="text-xs text-gray-400">{companyTagline}</p>}
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-2">Gallery</p>
                  <div className="grid grid-cols-3 gap-2">
                    {companyPhotos.slice(0, 6).map((photo, idx) => (
                      <img key={idx} src={photo.publicUrl ?? photo.fileUrl} alt={`Gallery ${idx + 1}`} className="h-20 w-full object-cover rounded border border-white/10" />
                    ))}
                    {companyPhotos.length === 0 && (
                      <p className="text-xs text-gray-500 col-span-3">Add photos to showcase your workplace.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </main>
  );
}
